/**
 * Sentry implementation of the Observability interface.
 *
 * Only instantiated when VITE_SENTRY_DSN is set at build time (see main.tsx) -
 * with no DSN the app keeps the console-based no-op impl, so this file is
 * tree-shaken to a thin wrapper in dev/preview builds. Keeping the Sentry SDK
 * import isolated here means the rest of the codebase never imports @sentry/react
 * directly and can be swapped/stubbed in one place.
 */
import * as Sentry from '@sentry/react';
import type { Observability, ErrorContext } from './index';
import { scrubEvent, scrubBreadcrumb } from './sentry-scrub';

/**
 * Call once, before the first React render, when a DSN is configured.
 * Performance tracing is off by default (errors-only) to keep the footprint
 * minimal; raise tracesSampleRate when an APM budget is approved.
 */
export function initSentry(dsn: string): void {
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0,
    // Never attach IP/cookies/headers automatically; we still scrub defensively
    // below in case tokens ride along in URLs or free text.
    sendDefaultPii: false,
    // Strip auth tokens / secrets from outgoing events + breadcrumbs. The
    // scrubbers mutate in place; return the original object to preserve types.
    beforeSend: (event) => {
      scrubEvent(event as unknown as Parameters<typeof scrubEvent>[0]);
      return event;
    },
    beforeBreadcrumb: (breadcrumb) => {
      scrubBreadcrumb(breadcrumb as unknown as Parameters<typeof scrubBreadcrumb>[0]);
      return breadcrumb;
    },
  });
}

function captureContext(ctx?: ErrorContext): Parameters<typeof Sentry.captureException>[1] {
  if (!ctx) return undefined;
  return {
    tags: ctx.tags,
    extra: ctx.extra,
    user: ctx.userPseudoId ? { id: ctx.userPseudoId } : undefined,
  };
}

export class SentryObservability implements Observability {
  captureError(err: unknown, ctx?: ErrorContext): void {
    Sentry.captureException(err, captureContext(ctx));
  }

  captureMessage(msg: string, ctx?: ErrorContext): void {
    Sentry.captureMessage(msg, captureContext(ctx));
  }

  addBreadcrumb(category: string, message: string, data?: Record<string, unknown>): void {
    Sentry.addBreadcrumb({ category, message, data });
  }
}
